<template>
  <div class="mb-3 overflow-hidden rounded-xl border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-900 transition-all duration-200">
    <!-- Collapsed row: icon, number/time, duration, chevron -->
    <div
      class="flex cursor-pointer items-center justify-between p-4 hover:bg-gray-50 dark:hover:bg-slate-800"
      @click="$emit('toggle', call.id)"
    >
      <div class="flex items-center gap-3">
        <div class="flex h-10 w-10 items-center justify-center rounded-full bg-gray-100 dark:bg-slate-800 text-gray-400">
          <svg
            v-if="callDirection === 'missed'"
            class="h-5 w-5 text-red-500"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 8l4 4m0 0l-4 4m4-4H4" />
          </svg>
          <svg
            v-else-if="callDirection === 'inbound'"
            class="h-5 w-5 text-green-500"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
          </svg>
          <svg
            v-else
            class="h-5 w-5 text-blue-500"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7l4-4m0 0l4 4m-4-4v18" />
          </svg>
        </div>

        <div class="flex flex-col">
          <h4 class="text-sm font-semibold text-gray-900 dark:text-slate-100">{{ displayTitle }}</h4>
          <div class="flex items-center gap-1.5 text-xs text-gray-500 dark:text-slate-400 mt-0.5">
            <span v-if="matchedName" class="font-medium">{{ actualNumber }}</span>
            <span v-if="matchedName" class="text-gray-300">&bull;</span>
            <span>{{ formattedTime }}</span>
          </div>
        </div>
      </div>

      <div class="flex items-center gap-2">
        <span class="text-xs font-medium text-gray-500 dark:text-slate-400">{{ callDuration }}</span>
        <svg
          class="h-4 w-4 text-gray-400 transition-transform duration-200"
          :class="{ 'rotate-180': expanded }"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
        </svg>
      </div>
    </div>

    <!-- Expanded action buttons -->
    <div v-show="expanded" class="border-t border-gray-100 dark:border-slate-800 bg-gray-50 dark:bg-slate-800 px-4 pb-4">
      <div class="mt-4 grid grid-cols-4 gap-2">

        <button
          class="flex flex-col items-center justify-center rounded-lg bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-700 p-3 transition hover:bg-gray-100 dark:hover:bg-slate-700"
          @click.stop="$emit('details', call)"
        >
          <svg class="mb-1.5 h-5 w-5 text-gray-600 dark:text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <span class="text-[10px] font-medium text-gray-600 dark:text-slate-400">Details</span>
        </button>

        <button
          class="flex flex-col items-center justify-center rounded-lg bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-700 p-3 transition hover:bg-gray-100 dark:hover:bg-slate-700"
          @click.stop="$emit('call', call)"
        >
          <svg class="mb-1.5 h-5 w-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
          </svg>
          <span class="text-[10px] font-medium text-gray-600 dark:text-slate-400">Call</span>
        </button>

        <button
          class="flex flex-col items-center justify-center rounded-lg bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-700 p-3 transition hover:bg-gray-100 dark:hover:bg-slate-700"
          @click.stop="$emit('summary', call)"
        >
          <svg class="mb-1.5 h-5 w-5 text-purple-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          <span class="text-[10px] font-medium text-gray-600 dark:text-slate-400">Summary</span>
        </button>

        <button
          class="flex flex-col items-center justify-center rounded-lg bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-700 p-3 transition hover:bg-gray-100 dark:hover:bg-slate-700"
          :class="{ 'pointer-events-none opacity-50': !hasRecording }"
          @click.stop="$emit('recording', call)"
        >
          <svg class="mb-1.5 h-5 w-5 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <span class="text-[10px] font-medium text-gray-600 dark:text-slate-400">Recording</span>
        </button>

      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import type { CallRecord } from '~/types/calls';
import { isCallMissed, isCallInbound } from '~/utils/calls';
import { useContactStore } from '~/stores/contactStore';

const props = withDefaults(defineProps<{
  call: CallRecord;
  expanded?: boolean;
}>(), {
  expanded: false,
});

defineEmits(['details', 'call', 'summary', 'recording', 'toggle']);

const contactStore = useContactStore();

const callDirection = computed(() => {
  if (isCallMissed(props.call)) return 'missed';
  if (isCallInbound(props.call)) return 'inbound';
  return 'outbound';
});

const actualNumber = computed(() => {
  if (isCallInbound(props.call)) {
    return props.call.callerId || props.call.callerNumber || 'Unknown';
  }
  return props.call.destinationNumber || 'Unknown';
});

const matchedName = computed(() => {
  const number = isCallInbound(props.call)
    ? (props.call.callerNumber || props.call.callerId || '')
    : (props.call.destinationNumber || '');
    
  if (number) {
    return contactStore.getNameForNumber(number);
  }
  return null;
});

const displayTitle = computed(() => matchedName.value || actualNumber.value);

const callDuration = computed(() => {
  const dur = props.call.billSec ?? 0;
  if (dur === 0) return '0s';
  const m = Math.floor(dur / 60);
  const s = dur % 60;
  return m > 0 ? `${m}m ${s}s` : `${s}s`;
});

const formattedTime = computed(() => {
  const d = new Date(props.call.startEpoch * 1000);
  return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
});

// Summary is available if the RTDB summary node has text content
const hasSummary = computed(() => !!props.call.summary?.text);

// Recording is available if the `rec` RTDB marker exists or a full `recording` object is present
const hasRecording = computed(() => !!(props.call.rec?.ts || props.call.recording?.objectName));
</script>
